import React, { useRef, useState } from 'react';

import Column from '@amzn/meridian/column';
import Popover, { PopoverHeader } from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import { type TaskConflict } from 'src/services/jmr/types';
import {
  canJobBeModified,
  ConflictColorCode,
  createDataCyForJob,
  hasJobBeenModified,
  isJobAtCapacity,
  type TableDataRow,
} from 'src/utils/jmrUtils';
import { getRequirementColor, getRequirementQuantity } from 'src/utils/placementSelectionUtils';

interface ConflictTagProps {
  jobMatch: TableDataRow;
  exceededRestrictionIds: Set<number>;
}

const ConflictTag = ({ jobMatch, exceededRestrictionIds }: ConflictTagProps) => {
  const buttonRef = useRef();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const pluralizeString = (str: string, quantity: number) => `${str}${quantity > 1 ? 's' : ''}`;
  /** closure capturing exceededRestrictions for brevity in tsx attribute */
  const _getRequirementColor = (conflict: TaskConflict) => getRequirementColor(conflict, exceededRestrictionIds);

  const getModalText = () => {
    const conflictType = getConflictType();
    const numConflicts = jobMatch.conflicts?.length || 0;

    if (hasJobBeenModified(jobMatch)) {
      return (
        <Text>
          <strong>
            {numConflicts} job {pluralizeString('requirement', numConflicts)}
          </strong>{' '}
          have been modified to allow the associate to work the full shift.
        </Text>
      );
    }

    if (conflictType === ConflictColorCode.NO_CONFLICTS || conflictType === ConflictColorCode.IMPERFECT_CONFLICTS) {
      return (
        <Text>Based on restrictions the associate can perform this job for the full shift without any conflict.</Text>
      );
    } else if (
      conflictType === ConflictColorCode.UNSAFE_CONFLICTS_BUT_MODIFICATIONS_AVAILABLE ||
      conflictType === ConflictColorCode.SAFE_CONFLICTS
    ) {
      return (
        <Text>
          Based on restrictions the maximum amount of time the associate can perform this job is:{' '}
          <strong>
            {jobMatch.maximumHoursAssociateCanDo} hrs. {numConflicts} job {pluralizeString('requirement', numConflicts)}
          </strong>{' '}
          must be modified to allow the associate to work the full shift
          {!canJobBeModified(jobMatch) ? <>, but no modifications are present.</> : <>.</>}
        </Text>
      );
    } else if (ConflictColorCode.UNSAFE_CONFLICTS_AND_NO_MODIFICATIONS_AVAILABLE) {
      return (
        <Text>Based on restrictions the associate cannot perform this job and modifications are unavailable.</Text>
      );
    }
    return <></>;
  };

  const getConflictType = () => {
    if (jobMatch.perfectMatch || hasJobBeenModified(jobMatch)) {
      return ConflictColorCode.NO_CONFLICTS;
    } else if (!jobMatch.conflicts?.length) {
      return ConflictColorCode.IMPERFECT_CONFLICTS;
    } else if (jobMatch.maximumHoursAssociateCanDo > 0) {
      return ConflictColorCode.SAFE_CONFLICTS;
    } else {
      return canJobBeModified(jobMatch)
        ? ConflictColorCode.UNSAFE_CONFLICTS_BUT_MODIFICATIONS_AVAILABLE
        : ConflictColorCode.UNSAFE_CONFLICTS_AND_NO_MODIFICATIONS_AVAILABLE;
    }
  };

  return (
    <div
      onMouseLeave={() => setIsPopoverOpen(false)}
      onMouseEnter={() => setIsPopoverOpen(true)}
      style={{ zIndex: '1' }} // Keep this component's margin on top of the Maximum Hrs text for popover consistency
    >
      <div
        className={'conflictPopover__tag'}
        data-cy={`${createDataCyForJob(jobMatch.name)}-conflictTag`}
        aria-label={`${jobMatch.name} Conflict Tag`}
      >
        <Tag ref={buttonRef} type={getConflictType()}>
          {hasJobBeenModified(jobMatch) ? 'Modified' : `${jobMatch?.conflicts?.length || 0} Conflicts`}
        </Tag>
      </div>

      {
        <Popover
          aria-label={`${jobMatch.name} Conflict Popover`}
          anchorNode={buttonRef.current}
          open={isPopoverOpen}
          onClose={() => setIsPopoverOpen(false)}
          position="top"
        >
          <PopoverHeader closeLabel="Close">Requirement Conflicts</PopoverHeader>

          <Column data-cy={`${createDataCyForJob(jobMatch.name)}-conflictDetails`}>
            {!isJobAtCapacity(jobMatch) ? (
              <>
                {getModalText()}
                <Column spacingInset={'none'} spacing={'200'}>
                  {jobMatch.conflicts?.map((c) => (
                    <Row spacing="200" key={c.taskId}>
                      <Text color={_getRequirementColor(c)} type="h100">
                        {c.taskName}
                      </Text>
                      {c.requirementProportion && (
                        <Text color={_getRequirementColor(c)}>({getRequirementQuantity(jobMatch, c)} of shift)</Text>
                      )}
                    </Row>
                  ))}
                </Column>
              </>
            ) : (
              <Text>Job is at capacity.</Text>
            )}
          </Column>
        </Popover>
      }
    </div>
  );
};

export default ConflictTag;
